import { useTranslation } from "react-i18next";

const kpis = [
  {
    labelKey: "roi.kpi0_label",
    deltaKey: "roi.kpi0_delta",
    icon: "ri-line-chart-line",
  },
  {
    labelKey: "roi.kpi1_label",
    deltaKey: "roi.kpi1_delta",
    icon: "ri-arrow-down-line",
  },
  {
    labelKey: "roi.kpi2_label",
    deltaKey: "roi.kpi2_delta",
    icon: "ri-user-add-line",
  },
  {
    labelKey: "roi.kpi3_label",
    deltaKey: "roi.kpi3_delta",
    icon: "ri-timer-line",
  },
];

const cases = [
  {
    industryKey: "roi.case0_industry",
    logo: "ri-shopping-bag-3-line",
    quoteKey: "roi.case0_quote",
    authorKey: "roi.case0_author",
    roleKey: "roi.case0_role",
    metrics: [
      { kKey: "roi.case0_metric0_k", vKey: "roi.case0_metric0_v" },
      { kKey: "roi.case0_metric1_k", vKey: "roi.case0_metric1_v" },
      { kKey: "roi.case0_metric2_k", vKey: "roi.case0_metric2_v" },
    ],
    avatar:
      "/img/avatar-marta.jpg",
  },
  {
    industryKey: "roi.case1_industry",
    logo: "ri-cloud-line",
    quoteKey: "roi.case1_quote",
    authorKey: "roi.case1_author",
    roleKey: "roi.case1_role",
    metrics: [
      { kKey: "roi.case1_metric0_k", vKey: "roi.case1_metric0_v" },
      { kKey: "roi.case1_metric1_k", vKey: "roi.case1_metric1_v" },
      { kKey: "roi.case1_metric2_k", vKey: "roi.case1_metric2_v" },
    ],
    avatar:
      "/img/avatar-david.jpg",
  },
  {
    industryKey: "roi.case2_industry",
    logo: "ri-hotel-line",
    quoteKey: "roi.case2_quote",
    authorKey: "roi.case2_author",
    roleKey: "roi.case2_role",
    metrics: [
      { kKey: "roi.case2_metric0_k", vKey: "roi.case2_metric0_v" },
      { kKey: "roi.case2_metric1_k", vKey: "roi.case2_metric1_v" },
      { kKey: "roi.case2_metric2_k", vKey: "roi.case2_metric2_v" },
    ],
    avatar:
      "/img/case-1.jpg",
  },
];

const kpiValues = [
  { value: "3.8x" },
  { value: "-42%" },
  { value: "+1.2K" },
  { value: "8s" },
];

export default function RoiImpact() {
  const { t } = useTranslation();

  return (
    <section id="roi" className="relative py-24 md:py-32 bg-background-50 overflow-hidden">
      <div className="absolute -top-24 right-0 w-[520px] h-[520px] radial-glow opacity-30 pointer-events-none"></div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold">
              <span className="w-2 h-2 rounded-full bg-primary-600"></span>
              {t("roi.badge")}
            </span>
            <h2 className="mt-5 font-heading text-3xl md:text-5xl font-bold leading-tight">
              <span style={{ color: "#215E88" }}>{t("roi.h2_part1")}</span>{" "}
              <span
                style={{
                  background: "linear-gradient(90deg, #215E88, #02B36D)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t("roi.h2_gradient")}
              </span>
            </h2>
            <p className="mt-4 text-base md:text-lg leading-relaxed" style={{ color: "#215E88" }}>
              {t("roi.description")}
            </p>
          </div>
        </div>

        {/* KPI grid */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {kpis.map((k, i) => (
            <div key={k.labelKey} className="glass-card glow-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${k.icon} text-xl`}></i>
                  </span>
                </span>
                <span className="text-[11px] font-semibold bg-accent-100 px-2 py-0.5 rounded-full" style={{ color: "#215E88" }}>
                  {t(k.deltaKey)}
                </span>
              </div>
              <p className="mt-5 font-heading text-3xl md:text-4xl font-bold" style={{ background: "linear-gradient(90deg, #215E88, #02B36D)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                {kpiValues[i].value}
              </p>
              <p className="mt-1 text-xs md:text-sm" style={{ color: "#215E88" }}>{t(k.labelKey)}</p>
            </div>
          ))}
        </div>

        {/* Case studies */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          {cases.map((c) => (
            <div key={c.authorKey} className="glass-card glow-border rounded-lg p-6 md:p-7 flex flex-col justify-between h-full">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${c.logo} text-xl`}></i>
                  </span>
                </span>
                <span className="text-xs font-semibold" style={{ color: "#215E88" }}>{t(c.industryKey)}</span>
              </div>
              <p className="mt-5 text-sm md:text-base leading-relaxed" style={{ color: "#215E88" }}>
                &ldquo;{t(c.quoteKey)}&rdquo;
              </p>
              <div className="mt-6 grid grid-cols-3 gap-2">
                {c.metrics.map((m) => (
                  <div key={m.vKey} className="rounded-md bg-background-100 py-2.5 text-center">
                    <p className="font-heading text-base font-bold text-primary-700">{t(m.kKey)}</p>
                    <p className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: "#215E88" }}>
                      {t(m.vKey)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-background-200/70 flex items-center gap-3">
                <img
                  src={c.avatar}
                  alt={t(c.authorKey)}
                  className="w-11 h-11 rounded-full object-cover object-top border border-background-200"
                />
                <div>
                  <p className="text-sm font-semibold text-foreground-950">{t(c.authorKey)}</p>
                  <p className="text-xs" style={{ color: "#215E88" }}>{t(c.roleKey)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}